import React from "react";
import {SurveyAnswerUi, SurveyInListUi, SurveyQuestion, SurveyTrigger} from "../../../../../api/generated";
import {formatDateTime} from "../../../../../utils/dateTimeUtil";
import {surveyTranscriptLink} from "../../feedbackTranscriptLink";
import {SurveyScoreChip} from "./FeedbackRatingChip";
import {OpenTranscriptButton} from "./OpenTranscriptButton";
import styles from "./FeedbackTable.module.css";

const QUESTION_ORDER: SurveyQuestion[] = [SurveyQuestion.Helpfulness, SurveyQuestion.Accuracy, SurveyQuestion.Clarity];

const QUESTION_LABELS: Record<SurveyQuestion, string> = {
    [SurveyQuestion.Helpfulness]: "Helpfulness",
    [SurveyQuestion.Accuracy]: "Accuracy",
    [SurveyQuestion.Clarity]: "Clarity",
};

export const TRIGGER_LABELS: Record<SurveyTrigger, string> = {
    [SurveyTrigger.EndChat]: "End of chat",
    [SurveyTrigger.MessageCount]: "After N messages",
    [SurveyTrigger.Booking]: "After booking",
};

interface SurveyTableProps {
    surveys: SurveyInListUi[]
}

const SurveyComments: React.FC<{ answers: SurveyAnswerUi[] }> = ({answers}) => {
    const withComment = answers.filter(answer => answer.comment);
    if (withComment.length === 0) {
        return <span className={styles.muted}>No comment</span>;
    }
    return <>
        {withComment.map(answer => <div key={answer.question} className={styles.comment}>
            <span className={styles.commentQuestion}>{QUESTION_LABELS[answer.question]}: </span>
            “{answer.comment}”
        </div>)}
    </>
}

export const SurveyTable: React.FC<SurveyTableProps> = ({surveys}) => {
    return <div role="table" aria-label="Survey results" className={styles.table}>
        <div role="row" className={`${styles.surveyRow} ${styles.headerRow}`}>
            <span role="columnheader">Date</span>
            <span role="columnheader">Assistant</span>
            <span role="columnheader">Trigger</span>
            {QUESTION_ORDER.map(question =>
                <span role="columnheader" key={question}>{QUESTION_LABELS[question]}</span>)}
            <span role="columnheader">Comment</span>
            <span role="columnheader"/>
        </div>
        {surveys.length === 0 && <div className={styles.empty}>No surveys match these filters.</div>}
        {surveys.map(survey => {
            const {date, time} = formatDateTime(survey.createdAt);
            const answersByQuestion = new Map(survey.answers.map(answer => [answer.question, answer]));
            return <div role="row" key={survey.id} className={styles.surveyRow}>
                <div role="cell" className={styles.cell}>
                    <div>{date}</div>
                    <div className={styles.secondary}>{time}</div>
                </div>
                <div role="cell" className={styles.cell}>{survey.botName ?? "Unknown Assistant"}</div>
                <div role="cell" className={`${styles.cell} ${styles.secondary}`}>{TRIGGER_LABELS[survey.trigger]}</div>
                {QUESTION_ORDER.map(question => {
                    const answer = answersByQuestion.get(question);
                    return <div role="cell" key={question} className={styles.cell}>
                        {answer ? <SurveyScoreChip rating={answer.rating}/> : <span className={styles.muted}>—</span>}
                    </div>
                })}
                <div role="cell" className={styles.cell}><SurveyComments answers={survey.answers}/></div>
                <div role="cell">
                    <OpenTranscriptButton to={surveyTranscriptLink(survey, survey.id)}/>
                </div>
            </div>
        })}
    </div>
}
